"use client"

import React, { useEffect, useMemo, useState } from "react"
import { ChevronDown, ChevronRight, X } from "lucide-react"
import { FilterSearchHomesResponse } from "@/features/homeowners/hooks/filter-search-homes.hook"
import { useSession } from "@/features/auth/context/session.context"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { getClaimedByDisplayName } from "@/features/homeowners/lib/claimed-by-display"
import { UserRole } from "@/shared/constants/user-roles"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { Button } from "@/shared/components/ui/button"
import { Checkbox } from "@/shared/components/ui/checkbox"
import {
  DateRangePickerControl,
  formatLocalDateYmd,
  parseYmdToLocalDate,
} from "@/shared/components/form-inputs/date-range-picker-control"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"
import { useClaimedByReportFilters } from "../hooks/use-claimed-by-report-filters"
import type { PartnerRegionAssociation } from "@/features/partners/types/partner.type"
import { PartnerReportDialog } from "./partner-report-dialog"
import {
  buildClaimedWorkscopeItems,
  ClaimedWorkscopeItem,
  filterClaimedWorkscopeItems,
  formatCalendarDate,
  WORKSCOPE_DEFINITIONS,
} from "../lib/claimed-by-workscope-status"

type ClaimedByWorkscopeStatusProps = {
  allHomesData?: FilterSearchHomesResponse
  organizationRegions?: PartnerRegionAssociation[]
}

type ClaimedByOption = {
  label: string
  value: string
}

type OrganizationSummary = {
  organization: string
  homes: number
  total: number
  complete: number
  inProgress: number
  notStarted: number
  unknown: number
  percentComplete: number
  items: ClaimedWorkscopeItem[]
}

const ALL_CLAIMED_BY = "__all__"

export function normalizeOrganizationName(value: unknown): string {
  return getClaimedByDisplayName(value).toLowerCase()
}

function normalizeOrganizationNames(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(normalizeOrganizationNames)
  if (typeof value !== "string") return []

  const trimmed = value.trim()
  if (!trimmed) return []

  try {
    const parsed = JSON.parse(trimmed)
    if (parsed !== trimmed) return normalizeOrganizationNames(parsed)
  } catch {
    // Plain organization names are expected in normal report data.
  }

  const normalized = normalizeOrganizationName(trimmed)
  return normalized ? [normalized] : []
}

export function canViewPartnerReport(
  role: UserRole | undefined,
  profileOrganizationName: unknown,
  reportOrganizationName: unknown,
): boolean {
  if (role === UserRole.ADMIN || role === UserRole.BASE_LEADER) return true
  if (role !== UserRole.PARTNER) return false
  const profileOrganization = normalizeOrganizationName(profileOrganizationName)
  return Boolean(
    profileOrganization
    && normalizeOrganizationNames(reportOrganizationName).includes(profileOrganization),
  )
}

function summarizeByOrganization(items: ClaimedWorkscopeItem[]): OrganizationSummary[] {
  const summaries = new Map<string, OrganizationSummary>()

  items.forEach((item) => {
    const summary = summaries.get(item.organization) ?? {
      organization: item.organization,
      homes: 0,
      total: 0,
      complete: 0,
      inProgress: 0,
      notStarted: 0,
      unknown: 0,
      percentComplete: 0,
      items: [],
    }

    summary.total += 1
    summary.items.push(item)
    if (item.bucket === "complete") summary.complete += 1
    if (item.bucket === "inProgress") summary.inProgress += 1
    if (item.bucket === "notStarted") summary.notStarted += 1
    if (item.bucket === "unknown") summary.unknown += 1
    summaries.set(item.organization, summary)
  })

  return Array.from(summaries.values())
    .map((summary) => ({
      ...summary,
      homes: new Set(summary.items.map((item) => item.homeId)).size,
      percentComplete: summary.total > 0 ? Math.round((summary.complete / summary.total) * 100) : 0,
      items: summary.items.sort((a, b) => a.homeownerName.localeCompare(b.homeownerName)),
    }))
    .sort((a, b) => a.organization.localeCompare(b.organization))
}

function getVisibleDetailItems(summary: OrganizationSummary, role: UserRole | undefined, sessionSub: unknown, profileOrganizationName: unknown): ClaimedWorkscopeItem[] {
  if (role === UserRole.ADMIN || role === UserRole.BASE_LEADER) {
    return summary.items
  }

  if (role === UserRole.PARTNER) {
    const userOrganization = normalizeOrganizationName(profileOrganizationName)
    return userOrganization && userOrganization === normalizeOrganizationName(summary.organization)
      ? summary.items
      : []
  }

  if (role === UserRole.HOMEOWNER) {
    const userId = Number(sessionSub)
    if (!Number.isFinite(userId)) return []
    return summary.items.filter((item) => item.homeownerUserId === userId)
  }

  return []
}

export default function ClaimedByWorkscopeStatus({
  allHomesData,
  organizationRegions = [],
}: ClaimedByWorkscopeStatusProps) {
  const { session, profile } = useSession()
  const { regionId } = useRegionId()
  const [claimedByOptions, setClaimedByOptions] = useState<ClaimedByOption[]>([
    { label: "Unclaimed", value: "Unclaimed" },
    { label: "No Need", value: "No Need" },
    { label: "Homeowner", value: "Homeowner" },
  ])
  const [claimedByLoading, setClaimedByLoading] = useState(false)
  const [selectedClaimedBy, setSelectedClaimedBy] = useState(ALL_CLAIMED_BY)
  const [expandedOrganizations, setExpandedOrganizations] = useState<Set<string>>(new Set())
  const { filters, setFilters, clearFilters, hasActiveFilters } = useClaimedByReportFilters()

  useEffect(() => {
    let isMounted = true

    const fetchClaimedByOptions = async () => {
      setClaimedByLoading(true)
      try {
        const params = new URLSearchParams()
        if (regionId) {
          params.set("regionId", regionId.toString())
        }

        const url = `/organizations/options${params.toString() ? `?${params.toString()}` : ""}`
        const res = await authFetchJson<any[]>(url)
        if (!res.success) return
        if (!isMounted) return

        const data = (res.data as any)?.data ?? res.data
        const organizations = Array.isArray(data) ? data : []

        setClaimedByOptions([
          { label: "Unclaimed", value: "Unclaimed" },
          { label: "No Need", value: "No Need" },
          { label: "Homeowner", value: "Homeowner" },
          ...Array.from(
            new Set(organizations.map((org: any) => getClaimedByDisplayName(org.label)))
          ).map((orgName) => ({
            label: orgName,
            value: orgName,
          })),
        ])
      } finally {
        if (isMounted) {
          setClaimedByLoading(false)
        }
      }
    }

    fetchClaimedByOptions()

    return () => {
      isMounted = false
    }
  }, [regionId])

  const summaries = useMemo(() => {
    if (!allHomesData?.success || !Array.isArray(allHomesData.data)) return []
    const activeHomes = allHomesData.data.filter((home: any) => home.isReported === true)
    const items = buildClaimedWorkscopeItems(activeHomes)
    const filteredItems = selectedClaimedBy === ALL_CLAIMED_BY
      ? items
      : items.filter((item) => item.organization === getClaimedByDisplayName(selectedClaimedBy))
    return summarizeByOrganization(filteredItems)
  }, [allHomesData, selectedClaimedBy])

  const toggleExpanded = (organization: string) => {
    setExpandedOrganizations((current) => {
      const next = new Set(current)
      if (next.has(organization)) {
        next.delete(organization)
      } else {
        next.add(organization)
      }
      return next
    })
  }

  if (!allHomesData) {
    return <p className="py-8 text-center text-muted-foreground">Loading claimed-by workscope status...</p>
  }

  return (
    <div className="space-y-4">
      {session?.role !== UserRole.ADMIN && session?.role !== UserRole.BASE_LEADER && (
        <p className="text-xs text-muted-foreground">
          Homeowner detail is limited by role; summary totals remain visible.
        </p>
      )}

      <div className="grid gap-3 rounded-md border bg-muted/20 p-3 sm:grid-cols-2 xl:grid-cols-[minmax(14rem,1fr)_minmax(12rem,16rem)_minmax(16rem,20rem)_auto] xl:items-end">
        <div>
          <label className="mb-1 block text-sm font-medium">Work scopes</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full justify-between border-input font-normal text-foreground hover:bg-transparent"
              >
                {filters.workscopes.length === 0 ? (
                  <span className="truncate text-muted-foreground">All work scopes</span>
                ) : (
                  <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                    {filters.workscopes.slice(0, 2).map((workscope) => (
                      <span
                        key={workscope}
                        className="inline-flex min-w-0 max-w-32 shrink items-center gap-1 rounded bg-primary/10 px-2 py-1 text-xs text-primary"
                      >
                        <span className="truncate">{workscope}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={`Remove ${workscope}`}
                          className="shrink-0 rounded-sm hover:text-primary/70"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation()
                            setFilters((current) => ({
                              ...current,
                              workscopes: current.workscopes.filter((selected) => selected !== workscope),
                            }))
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") return
                            event.preventDefault()
                            event.stopPropagation()
                            setFilters((current) => ({
                              ...current,
                              workscopes: current.workscopes.filter((selected) => selected !== workscope),
                            }))
                          }}
                        >
                          <X className="h-3 w-3" />
                        </span>
                      </span>
                    ))}
                    {filters.workscopes.length > 2 && (
                      <span className="shrink-0 rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                        +{filters.workscopes.length - 2}
                      </span>
                    )}
                  </span>
                )}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
              <div className="max-h-72 space-y-1 overflow-y-auto">
                {WORKSCOPE_DEFINITIONS.map((definition) => {
                  const checked = filters.workscopes.includes(definition.label)
                  return (
                    <label
                      key={definition.label}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => {
                          setFilters((current) => ({
                            ...current,
                            workscopes: checked
                              ? current.workscopes.filter((workscope) => workscope !== definition.label)
                              : [...current.workscopes, definition.label],
                          }))
                        }}
                      />
                      {definition.label}
                    </label>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="relative">
          <label className="mb-1 block text-sm font-medium">Claimed By</label>
          <Select value={selectedClaimedBy} onValueChange={setSelectedClaimedBy}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="All Claimed By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLAIMED_BY}>All Claimed By</SelectItem>
              {claimedByOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {claimedByLoading && (
            <p className="absolute top-full mt-1 text-xs text-muted-foreground">
              Loading organizations...
            </p>
          )}
        </div>
        <DateRangePickerControl
          id="claimed-date-completed"
          name="claimed-date-completed"
          label="Date completed"
          placeholder="Pick a date range"
          triggerClassName="h-10"
          value={{
            from: parseYmdToLocalDate(filters.dateCompletedFrom),
            to: parseYmdToLocalDate(filters.dateCompletedTo),
          }}
          onChange={(range) => {
            setFilters((current) => ({
              ...current,
              dateCompletedFrom: range.from ? formatLocalDateYmd(range.from) : "",
              dateCompletedTo: range.to ? formatLocalDateYmd(range.to) : "",
            }))
          }}
        />
        {(hasActiveFilters || selectedClaimedBy !== ALL_CLAIMED_BY) && (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="h-10 justify-start px-2 xl:justify-center"
            onClick={() => {
              clearFilters()
              setSelectedClaimedBy(ALL_CLAIMED_BY)
            }}
          >
            <X className="mr-1 h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>

      {summaries.length === 0 ? (
        <p className="rounded-md border border-dashed p-6 text-center text-muted-foreground">
          No claimed workscopes found for this selection.
        </p>
      ) : (
        <Table className="w-full table-fixed text-xs sm:min-w-[720px] sm:table-auto sm:text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-20 w-[58%] bg-muted px-1 sm:w-auto sm:min-w-40 sm:px-2">Claimed By</TableHead>
              <TableHead className="w-[21%] px-1 text-right sm:w-auto sm:px-2">WS Total</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Complete</TableHead>
              <TableHead className="hidden text-right sm:table-cell">In Progress</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Not Started</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Unknown</TableHead>
              <TableHead className="hidden text-right sm:table-cell">% Complete</TableHead>
              <TableHead className="w-[21%] px-1 text-right sm:w-auto sm:px-2">Homes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summaries.map((summary) => {
              const visibleDetailItems = getVisibleDetailItems(
                summary,
                session?.role,
                session?.sub,
                profile?.organizationName,
              )
              const detailItems = filterClaimedWorkscopeItems(visibleDetailItems, filters)
              const canExpand = visibleDetailItems.length > 0
              const isExpanded = canExpand && expandedOrganizations.has(summary.organization)
              const canViewReport = canViewPartnerReport(
                session?.role,
                profile?.organizationName,
                summary.organization,
              )
              const contactEmail = organizationRegions.find(
                (association) =>
                  normalizeOrganizationName(association.organization?.name)
                  === normalizeOrganizationName(summary.organization),
              )?.contactEmail
              return (
                <React.Fragment key={summary.organization}>
                  <TableRow>
                    <TableCell className="sticky left-0 z-10 w-[58%] bg-muted px-1 sm:w-auto sm:min-w-40 sm:px-2">
                      {canExpand ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto max-w-full justify-start px-0 text-xs font-semibold sm:text-sm"
                          onClick={() => toggleExpanded(summary.organization)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="mr-1 h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
                          ) : (
                            <ChevronRight className="mr-1 h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
                          )}
                          <span className="truncate">{summary.organization}</span>
                        </Button>
                      ) : (
                        <span className="block truncate font-semibold">{summary.organization}</span>
                      )}
                    </TableCell>
                    <TableCell className="w-[21%] px-1 text-right sm:w-auto sm:px-2">{summary.total}</TableCell>
                    <TableCell className="hidden text-right text-green-700 sm:table-cell">{summary.complete}</TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{summary.inProgress}</TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{summary.notStarted}</TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{summary.unknown}</TableCell>
                    <TableCell className="hidden text-right font-medium sm:table-cell">{summary.percentComplete}%</TableCell>
                    <TableCell className="w-[21%] px-1 text-right sm:w-auto sm:px-2">{summary.homes}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={8} className="bg-muted/30 p-0">
                        <div className="max-h-80 overflow-auto p-3">
                          {canViewReport && regionId && (
                            <div className="mb-3 flex justify-end">
                              <PartnerReportDialog
                                regionId={regionId}
                                organizationName={summary.organization}
                                filters={filters}
                                contactEmail={contactEmail}
                              />
                            </div>
                          )}
                          <Table noScrollWrapper>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Homeowner</TableHead>
                                <TableHead>Address</TableHead>
                                <TableHead>Workscope</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Date Completed</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detailItems.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                                    No workscopes match the selected filters.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                detailItems.map((item, index) => (
                                  <TableRow key={`${summary.organization}-${item.homeId}-${item.workscope}-${index}`}>
                                    <TableCell>{item.homeownerName}</TableCell>
                                    <TableCell>{item.address}</TableCell>
                                    <TableCell>{item.workscope}</TableCell>
                                    <TableCell>{item.status}</TableCell>
                                    <TableCell>{formatCalendarDate(item.dateCompleted)}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
